import axios from 'axios';
import axiosRetry from 'axios-retry';
import { authService } from './services/authService';
import { DIRECTUS_INSTANCE } from './config/directus';
import {
  getWorkspaceToken as getWorkspaceTokenFromWidgetApi,
  createWidgetThread as createWidgetThreadFromWidgetApi,
  sendWidgetMessage as sendWidgetMessageFromWidgetApi
} from './services/widgetChatApi';
import { formatDisplayDateTime } from './utils/dateFormatting';
// Logger removed - using inline conditions for production builds

export { DIRECTUS_INSTANCE } from './config/directus';

// API Endpoints
export const DIRECTUS_DATASETS_ENDPOINT = `${DIRECTUS_INSTANCE}/items/data_sets`;
export const DIRECTUS_FILES_ENDPOINT = `${DIRECTUS_INSTANCE}/files`;
export const DIRECTUS_PROJECTS_ENDPOINT = `${DIRECTUS_INSTANCE}/items/projects`;
export const OPENAI_COMPLETIONS_INSTANCE = process.env.REACT_APP_OPENAI_COMPLETIONS_INSTANCE;
export const API_URL = process.env.REACT_APP_API_URL;

export const DIRECTUS_PERSONAS_ENDPOINT = `${DIRECTUS_INSTANCE}/items/personas`;
export const DIRECTUS_ARTICLES_ENDPOINT = `${DIRECTUS_INSTANCE}/items/articles`;
export const DIRECTUS_HEATMAPS_ENDPOINT = `${DIRECTUS_INSTANCE}/items/heatmaps`;
export const DIRECTUS_ONBOARDING_ENDPOINT = `${DIRECTUS_INSTANCE}/items/onboarding_profiling`;
export const DIRECTUS_CHAT_SESSIONS_ENDPOINT = `${DIRECTUS_INSTANCE}/items/chat_sessions`;

// Provider API keys (OpenAI, Anthropic, Pinecone, SerpAPI, ...) must never be
// read here: CRA inlines every REACT_APP_* value into the public JS bundle.
// Provider calls go through the authenticated article-queue API instead.
export const ASSISTANT_ID = process.env.REACT_APP_ASSISTANT_ID;

// Pinecone Configuration
export const PINECONE_ENVIRONMENT = process.env.REACT_APP_PINECONE_ENVIRONMENT;
export const PINECONE_INDEX_NAME = process.env.REACT_APP_PINECONE_INDEX_NAME;

export const OPENAI_API_URL = 'https://api.openai.com/v1';
export const VECTOR_STORE_ID = process.env.REACT_APP_VECTOR_STORE_ID || 'vs_0aZJbV6SHHz6zKagR2osdkio';

// Create an Axios instance for Directus API
export const directusAPI = axios.create({
  baseURL: DIRECTUS_INSTANCE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Set up axios-retry
axiosRetry(directusAPI, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => error.response?.status === 429 || error.response?.status === 503,
});

// DEBUG: Log all PATCH requests to articles
directusAPI.interceptors.request.use((config) => {
  if (config.method === 'patch' && config.url?.includes('/items/articles/')) {
    console.log('🌐 DIRECTUS PATCH REQUEST:', {
      url: config.url,
      data: config.data,
      article_linked_structure: config.data?.article_linked_structure,
      type: typeof config.data?.article_linked_structure
    });
  }
  return config;
});

export const fetchCurrentUser = async (fields = ['id']) => {
  const fieldsParam = Array.isArray(fields) ? fields.join(',') : fields;
  const response = await directusAPI.get('/users/me', {
    params: { fields: fieldsParam }
  });
  return response.data?.data ?? null;
};

// Add an interceptor to include the token in every request
directusAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);



// Generic function to handle errors from API requests
const handleApiError = (error) => {
  if (error.response) {
    // process.env.NODE_ENV === "development" && console.error('Server responded with an error:', error.response.data);
    // process.env.NODE_ENV === "development" && console.error('Status code:', error.response.status);
  } else if (error.request) {
    // process.env.NODE_ENV === "development" && console.error('No response received:', error.request);
  } else {
    // process.env.NODE_ENV === "development" && console.error('Error setting up request:', error.message);
  }
  throw error;
};

export const queryPinecone = async (vector, topK = 5) => {
  try {
    const response = await axios.post(`${API_URL}/query`, { vector, topK });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};


export const saveProjectData = async (dataToSave) => {
  try {
    const response = await directusAPI.post('/items/projects', dataToSave);
    // process.env.NODE_ENV === "development" && console.log('Project data saved successfully:', response.data);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getWorkspaceToken = (...args) => getWorkspaceTokenFromWidgetApi(...args);
export const createWidgetThread = (...args) => createWidgetThreadFromWidgetApi(...args);
export const sendWidgetMessage = (...args) => sendWidgetMessageFromWidgetApi(...args);

const WORKER_REMOTE_BASE = 'https://api.prosecaptain.com/workers/api';

// Lokalny serwer API article-queue (tylko gdy front działa na localhost).
// Port ustawiasz w frontend/.env.development.local, patrz LOCAL_PORTS.md.
export const LOCAL_WORKER_ORIGIN = process.env.REACT_APP_LOCAL_WORKER_API_URL || 'http://localhost:3002';
export const LOCAL_WORKER_API_BASE = `${LOCAL_WORKER_ORIGIN}/api`;

const getWorkerApiBases = () => {
  const isLocalhost = window.location.hostname === 'localhost';
  return isLocalhost
    ? [LOCAL_WORKER_API_BASE, WORKER_REMOTE_BASE]
    : [WORKER_REMOTE_BASE];
};

const fetchWorkerApi = async (path, options = {}) => {
  const bases = getWorkerApiBases();
  let lastError = null;

  for (const base of bases) {
    const url = `${base}${path}`;
    try {
      const response = await fetch(url, options);
      return response;
    } catch (error) {
      lastError = error;
      process.env.NODE_ENV === "development" && console.warn(`Worker API unavailable at ${url}, trying next endpoint...`);
    }
  }

  throw lastError || new Error(`All Worker API endpoints failed for path: ${path}`);
};

export const createThread = async () => {
  console.log('🚀 USING NEW createThread FROM WORKER!'); // DEBUG LOG
  try {
    const response = await fetchWorkerApi('/thread', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to create thread');
    }

    return data.threadId;
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Error creating thread:', error);
    throw error;
  }
};

export const addMessageToThread = async (threadId, message) => {
  try {
    const response = await fetchWorkerApi('/message', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        threadId: threadId,
        message: {
          role: message.role,
          content: message.content
        }
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      // If thread not found, create a new one and retry
      if (data.error && data.error.includes('No thread found')) {
        process.env.NODE_ENV === "development" && console.log('Thread not found, creating new thread...');
        const newThreadId = await createThread();
        return await addMessageToThread(newThreadId, message);
      }
      throw new Error(data.error || 'Failed to add message to thread');
    }

    // Return data in format expected by existing code
    return {
      id: data.messageId,
      thread_id: data.threadId,
      role: message.role,
      content: message.content
    };
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Error adding message to thread:', error);
    throw error;
  }
};

export const runAssistant = async (threadId, assistantId, options = {}) => {
  try {
    const requestBody = {
      threadId: threadId,
      assistantId: assistantId,
      options: {
        instructions: options.instructions,
        tools: options.tools || [{ "type": "file_search" }],
        metadata: {}
      }
    };

    // Convert vector_store_ids to a string if present
    if (options.file_search?.vector_store_ids?.length > 0) {
      requestBody.options.metadata.file_search = JSON.stringify({
        vector_store_ids: options.file_search.vector_store_ids
      });
    }

    process.env.NODE_ENV === "development" && console.log('Run request payload:', JSON.stringify(requestBody, null, 2));

    // Get token for authorization
    const token = localStorage.getItem('directus_token');

    const response = await fetchWorkerApi('/run', {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to run assistant');
    }

    process.env.NODE_ENV === "development" && console.log('Run response:', data);
    
    return data.runId;
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Run assistant error:', error);
    throw error;
  }
};

export const checkRunStatus = async (threadId, runId) => {
  try {
    const response = await fetchWorkerApi(`/run-status/${threadId}/${runId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to check run status');
    }

    // Return data in format expected by existing code
    return data.data;
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Error checking run status:', error);
    throw error;
  }
};

// Submit tool outputs for function calling
export const submitToolOutputs = async (threadId, runId, toolOutputs) => {
  try {
    process.env.NODE_ENV === "development" && console.log('Submitting tool outputs:', { threadId, runId, toolOutputs });

    const response = await fetchWorkerApi('/submit-tool-outputs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        threadId: threadId,
        runId: runId,
        toolOutputs: toolOutputs
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to submit tool outputs');
    }

    process.env.NODE_ENV === "development" && console.log('Tool outputs submitted successfully:', data);
    
    // Return data in format expected by existing code
    return data.data;
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Error submitting tool outputs:', error);
    throw error;
  }
};

export const listMessages = async (threadId) => {
  try {
    const response = await fetchWorkerApi(`/messages/${threadId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to list messages');
    }

    // Return data in format expected by existing code (with .data property)
    return { data: data.data };
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Error listing messages:', error);
    throw error;
  }
};

export const getAssistantId = async () => {
  try {
    const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
    const userId = localStorage.getItem('user_id');

    if (!token || !userId) {
      return ASSISTANT_ID;
    }

    const userResponse = await directusAPI.get(`/users/${userId}`, {
      params: { fields: ['linked_workspace'] }
    });

    const workspaceId = userResponse.data.data.linked_workspace;

    if (workspaceId) {
      const workspaceResponse = await directusAPI.get(`/items/workspace/${workspaceId}`, {
        params: { fields: ['assistant_id'] }
      });

      const workspaceAssistantId = workspaceResponse.data.data.assistant_id;

      if (workspaceAssistantId) {
        return workspaceAssistantId;
      }
    }

    return ASSISTANT_ID;
  } catch (error) {
    // process.env.NODE_ENV === "development" && console.error('Error fetching assistant ID:', error);
    return ASSISTANT_ID;
  }
};

export const getVectorStoreIdForWorkspace = async (workspaceId) => {
  if (!workspaceId) {
    return getVectorStoreId();
  }

  try {
    const workspaceResponse = await directusAPI.get(`/items/workspace/${workspaceId}`, {
      params: { fields: ['vector_store_id'] }
    });

    const workspaceVectorStoreId = workspaceResponse.data.data.vector_store_id;
    if (workspaceVectorStoreId) {
      return workspaceVectorStoreId;
    }
  } catch (error) {
    process.env.NODE_ENV === 'development' && console.error('Error fetching workspace vector store ID:', error);
  }

  return getVectorStoreId();
};

export const getVectorStoreId = async () => {
  try {
    const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
    const userId = localStorage.getItem('user_id');

    if (!token || !userId) {
      return VECTOR_STORE_ID;
    }

    const userResponse = await directusAPI.get(`/users/${userId}`, {
      params: { fields: ['linked_workspace'] }
    });

    const workspaceId = userResponse.data.data.linked_workspace;
    if (workspaceId) {
      return getVectorStoreIdForWorkspace(workspaceId);
    }

    return VECTOR_STORE_ID;
  } catch (error) {
    // process.env.NODE_ENV === "development" && console.error('Error fetching vector store ID:', error);
    return VECTOR_STORE_ID;
  }
};

// Handle API errors globally
directusAPI.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshSuccessful = await authService.refreshToken();
      if (refreshSuccessful) {
        originalRequest.headers['Authorization'] = `Bearer ${authService.getToken()}`;
        return directusAPI(originalRequest);
      }
      
      // If refresh failed, logout
      window.dispatchEvent(new CustomEvent('auth:logout'));
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

export const ITEMS_PER_PAGE = 50;

export const fetchArticles = async (token, userId, currentPage, workspaceId, itemsPerPage = ITEMS_PER_PAGE) => {
  try {
    const filter = {
      _and: [
        { user_created: { id: { _eq: userId } } }
      ]
    };

    // 🆕 UPDATED: Filter by article_linked_workspace (M2O field) only if workspace exists
    if (workspaceId) {
      // Show only articles for this workspace (including NULL workspace for backward compatibility)
      filter._and.push({
        _or: [
          { article_linked_workspace: { _eq: workspaceId } },
          { article_linked_workspace: { _null: true } }
        ]
      });
      
      console.log('📊 ARTICLES FILTER: Filtering by workspace (including legacy articles)', { workspaceId, userId });
    } else {
      // If user doesn't have workspace, show all their articles (legacy behavior)
      console.log('📊 ARTICLES FILTER: No workspace - showing all user articles (legacy mode)', { userId });
    }

    // Keep archived items out of both payload and meta counts.
    filter._and.push({ status: { _neq: 'archived' } });

    const response = await axios.get(`${DIRECTUS_ARTICLES_ENDPOINT}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        filter,
        limit: itemsPerPage,
        page: currentPage,
        sort: '-date_created',
        meta: 'total_count,filter_count',
        // 🆕 Added article_linked_workspace to fields
        fields: '*,user_created.first_name,user_created.email,article_linked_workspace.workspace_name,type'
      }
    });
    return response.data;
  } catch (error) {
    // process.env.NODE_ENV === "development" && console.error('Error fetching articles:', error);
    throw error;
  }
};

export const formatDate = (dateString) => {
  return formatDisplayDateTime(dateString, localStorage.getItem('i18nextLng') || 'en');
};

// Pomocnicza funkcja do konwersji workspace_id
export const normalizeWorkspaceId = (workspaceId) => {
  process.env.NODE_ENV === "development" && console.log('DEBUG - normalizing workspace_id:', { input: workspaceId, type: typeof workspaceId });
  
  // Jeśli brak workspace_id
  if (workspaceId === null || workspaceId === undefined) {
    process.env.NODE_ENV === "development" && console.warn('No workspace_id provided to normalize');
    return null;
  }
  
  // Jeśli już jest liczbą, zwróć ją
  if (typeof workspaceId === 'number') {
    process.env.NODE_ENV === "development" && console.log('workspace_id is already a number, returning as is');
    return workspaceId;
  }
  
  // Jeśli jest stringiem, spróbuj przekonwertować
  if (typeof workspaceId === 'string') {
    if (/^\d+$/.test(workspaceId)) {
      const parsed = parseInt(workspaceId, 10);
      process.env.NODE_ENV === "development" && console.log(`Converted workspace_id string "${workspaceId}" to number ${parsed}`);
      return parsed;
    } else {
      process.env.NODE_ENV === "development" && console.warn(`Cannot convert workspace_id "${workspaceId}" to number, it's not a numeric string`);
      return workspaceId; // Zwróć oryginał jeśli nie da się przekonwertować
    }
  }
  
  // Inny typ - zwróć oryginał
  process.env.NODE_ENV === "development" && console.warn(`Unexpected workspace_id type: ${typeof workspaceId}, value: ${workspaceId}`);
  return workspaceId;
};

// Zaktualizuj getChatSessions
export const getChatSessions = async (page = 1, sessionId = null) => {
  try {
    process.env.NODE_ENV === "development" && console.log('API DEBUG - getChatSessions start', { page, sessionId });
    
    // Jeśli podano konkretny sessionId, użyj prostego filtra
    if (sessionId) {
      process.env.NODE_ENV === "development" && console.log('API DEBUG - Fetching single session by ID:', sessionId);
      try {
        // Pobierz workspace_id z localStorage dla walidacji
        const workspaceId = localStorage.getItem('workspace_id');
        
        const response = await directusAPI.get(DIRECTUS_CHAT_SESSIONS_ENDPOINT, {
          params: {
            filter: JSON.stringify({ 
              _and: [
                { id: { _eq: sessionId } },
                ...(workspaceId ? [{
                  linked_workspace: { _eq: workspaceId }
                }] : [])
              ]
            }),
            fields: ['*', 'thread_id', 'user_created.id', 'user_created.first_name', 'user_created.last_name', 'user_created.email']
          }
        });
        process.env.NODE_ENV === "development" && console.log(`API DEBUG - getChatSessions response: ${response.data.data.length} sessions for ID ${sessionId} in workspace ${workspaceId}`);
        return response.data.data;
      } catch (error) {
        process.env.NODE_ENV === "development" && console.error('API ERROR - Error fetching session by ID:', error);
        return [];
      }
    }
    
    // Pobieramy wszystkie sesje z relacją user_created -> linked_workspace
    try {
      // Pobierz current workspace_id z localStorage
      const workspaceId = localStorage.getItem('workspace_id');
      process.env.NODE_ENV === "development" && console.log('API DEBUG - Current workspace_id from localStorage:', workspaceId);
      
      const response = await directusAPI.get(DIRECTUS_CHAT_SESSIONS_ENDPOINT, {
        params: {
          sort: '-date_created',
          fields: ['*', 'thread_id', 'user_created.linked_workspace']
        }
      });
      
      // Jeśli mamy workspace_id, filtrujemy po stronie klienta
      let filteredData = response.data.data;
      if (workspaceId && filteredData && filteredData.length > 0) {
        process.env.NODE_ENV === "development" && console.log(`API DEBUG - Filtrowanie po workspace_id (${workspaceId}) poprzez relację user_created.linked_workspace`);
        
        filteredData = filteredData.filter(session => {
          // Jeśli brak user_created lub linked_workspace, zachowujemy sesję
          if (!session.user_created || !session.user_created.linked_workspace) {
            return true;
          }
          
          // Porównujemy workspace_id
          return String(session.user_created.linked_workspace) === String(workspaceId);
        });
        
        process.env.NODE_ENV === "development" && console.log(`API DEBUG - Po filtrowaniu: ${filteredData.length} z ${response.data.data.length} sesji`);
      } else {
        process.env.NODE_ENV === "development" && console.log(`API DEBUG - Brak filtrowania po workspace_id - zwracanie wszystkich sesji`);
      }
      
      return filteredData;
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('API ERROR - Error fetching all sessions:', error);
      return [];
    }
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('API ERROR - Error in getChatSessions:', error);
    process.env.NODE_ENV === "development" && console.error('API ERROR - Error details:', error.response?.data || error.message);
    return [];
  }
};

export const createChatSession = async (title, threadId, assistantId, vectorStoreId, workspaceId) => {
  try {
    const data = {
      title: title,
      thread_id: threadId,
      assistant_id: assistantId,
      vector_store_id: vectorStoreId,
      workspace_id: workspaceId,
      linked_workspace: workspaceId, // Explicitly set linked_workspace to match workspace_id
      date_updated: new Date().toISOString() // Ensure date_updated is set on creation
    };
    
    const response = await directusAPI.post(DIRECTUS_CHAT_SESSIONS_ENDPOINT, data);
    return response.data.data;
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Error creating chat session:', error);
    throw error;
  }
};

export const updateChatSession = async (sessionId, data) => {
  try {
    const response = await directusAPI.patch(`${DIRECTUS_CHAT_SESSIONS_ENDPOINT}/${sessionId}`, data);
    return response.data.data;
  } catch (error) {
    // process.env.NODE_ENV === "development" && console.error('Error updating chat session:', error);
    throw error;
  }
};

export const saveChatMessage = async (sessionId, messageData, threadId) => {
  try {
    const response = await directusAPI.post('/items/chat_messages', {
      session_id: sessionId,
      thread_id: threadId,
      role: messageData.role,
      content: messageData.content,
      timestamp: new Date().toISOString(),
      order: messageData.order
    });

    // Keep chat_sessions.messages_count in sync with actual message rows.
    // Some inbox views rely on this field and can show 0/null when stale.
    try {
      const countResponse = await directusAPI.get('/items/chat_messages', {
        params: {
          filter: { session_id: { _eq: sessionId } },
          aggregate: { count: 'id' }
        }
      });

      const nextCount = Number(countResponse?.data?.data?.[0]?.count?.id);
      if (Number.isFinite(nextCount)) {
        await directusAPI.patch(`${DIRECTUS_CHAT_SESSIONS_ENDPOINT}/${sessionId}`, {
          messages_count: nextCount
        });
      }
    } catch (syncError) {
      process.env.NODE_ENV === "development" && console.warn('messages_count sync failed:', syncError?.message || syncError);
    }

    return response.data.data;
  } catch (error) {
    // process.env.NODE_ENV === "development" && console.error('Error saving chat message:', error);
    throw error;
  }
};

// Zaktualizuj fetchChatSessions
export const fetchChatSessions = async (token, userId, page, workspaceId) => {
  try {
    process.env.NODE_ENV === "development" && console.log(`API DEBUG - fetchChatSessions z filtrowaniem po workspace poprzez user_created`, { workspaceId });
    
    // Pobierz dane z uwzględnieniem relacji user_created -> linked_workspace
    const params = {
      sort: '-date_created',
      page: page,
      limit: ITEMS_PER_PAGE,
      fields: ['*', 'user_created.first_name', 'user_created.last_name', 'user_created.email', 'user_created.linked_workspace']
    };
    
    process.env.NODE_ENV === "development" && console.log(`API DEBUG - Request params:`, params);
    
    // Pobieramy wszystkie sesje
    const response = await directusAPI.get(DIRECTUS_CHAT_SESSIONS_ENDPOINT, { params });
    
    // Filtrujemy po stronie klienta na podstawie relacji
    if (workspaceId && response.data.data && response.data.data.length > 0) {
      process.env.NODE_ENV === "development" && console.log(`API DEBUG - Filtrowanie po workspace_id (${workspaceId}) poprzez relację user_created.linked_workspace`);
      
      const filteredData = response.data.data.filter(session => {
        // Jeśli brak user_created lub linked_workspace, zachowujemy sesję
        if (!session.user_created || !session.user_created.linked_workspace) {
          return true;
        }
        
        // Porównujemy workspace_id (jako string)
        return String(session.user_created.linked_workspace) === String(workspaceId);
      });
      
      process.env.NODE_ENV === "development" && console.log(`API DEBUG - Po filtrowaniu: ${filteredData.length} z ${response.data.data.length} sesji`);
      
      // Aktualizujemy odpowiedź
      response.data.data = filteredData;
      if (response.data.meta) {
        response.data.meta.filter_count = filteredData.length;
      }
    }
    
    // Debugowanie odpowiedzi
    process.env.NODE_ENV === "development" && console.log(`API DEBUG - Response success:`, { 
      count: response.data.data?.length || 0,
      hasData: !!response.data.data,
      meta: response.data.meta
    });
    
    return response.data;
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('API ERROR - Error fetching chat sessions:', error);
    process.env.NODE_ENV === "development" && console.error('API ERROR - Error details:', error.response?.data || error.message);
    return { data: [], meta: { total_count: 0 } };
  }
};

export const getChatMessages = async (sessionId) => {
  try {
    if (!sessionId) {
      process.env.NODE_ENV === "development" && console.error('Session ID is required to fetch messages');
      return [];
    }
    
    const response = await directusAPI.get('/items/chat_messages', {
      params: {
        filter: { session_id: { _eq: sessionId } },
        sort: 'date_created,order',
        limit: 1000, // Zwiększmy limit, aby mieć pewność, że pobieramy wszystkie wiadomości
        fields: ['id', 'role', 'content', 'order', 'date_created', 'date_updated', 'valence']
      }
    });

    const messages = Array.isArray(response.data.data) ? response.data.data : [];
    const sortedMessages = [...messages].sort((a, b) => {
      const aDate = a?.date_created ? new Date(a.date_created).getTime() : NaN;
      const bDate = b?.date_created ? new Date(b.date_created).getTime() : NaN;

      if (!Number.isNaN(aDate) && !Number.isNaN(bDate) && aDate !== bDate) return aDate - bDate;
      if (!Number.isNaN(aDate) && Number.isNaN(bDate)) return -1;
      if (Number.isNaN(aDate) && !Number.isNaN(bDate)) return 1;

      const aUpdated = a?.date_updated ? new Date(a.date_updated).getTime() : NaN;
      const bUpdated = b?.date_updated ? new Date(b.date_updated).getTime() : NaN;
      if (!Number.isNaN(aUpdated) && !Number.isNaN(bUpdated) && aUpdated !== bUpdated) return aUpdated - bUpdated;
      if (!Number.isNaN(aUpdated) && Number.isNaN(bUpdated)) return -1;
      if (Number.isNaN(aUpdated) && !Number.isNaN(bUpdated)) return 1;

      const aOrder = a?.order != null ? Number(a.order) : Number.MAX_SAFE_INTEGER;
      const bOrder = b?.order != null ? Number(b.order) : Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;

      return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
    });

    process.env.NODE_ENV === "development" && console.log(`Retrieved ${sortedMessages.length} messages for session ${sessionId}`);
    return sortedMessages;
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Error fetching chat messages:', error);
    throw error;
  }
};

// OpenAI Files API endpoints
export const listOpenAIFiles = async () => {
  try {
    console.log('🔍 listOpenAIFiles called via worker');

    const token = localStorage.getItem('directus_token');
    const response = await fetchWorkerApi('/files', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to list files');
    }

    // TODO: Add vector store filtering later if needed
    return { data: data.data };
  } catch (error) {
    // process.env.NODE_ENV === "development" && console.error('OpenAI API Error:', error);
    throw error;
  }
};

export const getOpenAIFile = async (fileId) => {
  try {
    const token = localStorage.getItem('directus_token');
    const response = await fetchWorkerApi(`/files/${fileId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to get file');
    }

    return data.data;
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Error getting OpenAI file:', error);
    throw error;
  }
};

export const cancelVectorStoreBatch = async (vectorStoreId, batchId) => {
  try {
    const token = localStorage.getItem('directus_token');
    const response = await fetchWorkerApi('/vector-store-batch-cancel', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        vectorStoreId,
        batchId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to cancel vector store batch');
    }

    return data;
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Error cancelling vector store batch:', error);
    throw error;
  }
};

export const deleteOpenAIFile = async (fileId) => {
  try {
    const token = localStorage.getItem('directus_token');
    const response = await fetchWorkerApi(`/files/${fileId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to delete file');
    }

    return data;
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Error deleting OpenAI file:', error);
    throw error;
  }
};

export const deleteVectorStoreFile = async (fileId, vectorStoreIdOverride = null) => {
  const isLocalhost = window.location.hostname === 'localhost';
  
  try {
    const vectorStoreId = vectorStoreIdOverride || await getVectorStoreId();
    
    if (isLocalhost) {
      process.env.NODE_ENV === "development" && console.log(`🗑️ Starting deletion process for file: ${fileId}`);
      process.env.NODE_ENV === "development" && console.log(`📦 Using vector store: ${vectorStoreId}`);
    }
    
    const token = localStorage.getItem('directus_token');
    const response = await fetchWorkerApi(`/vector-store-files/${fileId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: JSON.stringify({
        vectorStoreId: vectorStoreId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to delete file from vector store');
    }

    if (isLocalhost) {
      process.env.NODE_ENV === "development" && console.log(`🎉 Deletion completed successfully for file: ${fileId}`);
      process.env.NODE_ENV === "development" && console.log(`📊 Results: Vector store: ${data.vectorStoreDeleted}, File: ${data.fileDeleted}`);
    }
    
    return data;
  } catch (error) {
    if (isLocalhost) {
      process.env.NODE_ENV === "development" && console.error(`💥 Error deleting file ${fileId}:`, error);
    }
    throw error;
  }
};

// Add after existing API endpoints

export const TEST_API_ENDPOINT = `${DIRECTUS_INSTANCE}/items/api_test`;

/**
 * Get the appropriate API worker URL based on environment
 * @param {string} endpoint - API endpoint path (e.g., '/api/regenerate-thumbnail')
 * @returns {string} - Full API URL
 */
export const getWorkerApiUrl = (endpoint) => {
  const isLocalhost = window.location.hostname === 'localhost';
  const baseUrl = isLocalhost
    ? LOCAL_WORKER_API_BASE
    : WORKER_REMOTE_BASE;

  const normalizedEndpoint = String(endpoint || '').startsWith('/')
    ? String(endpoint)
    : `/${String(endpoint || '')}`;

  // Accept both '/api/foo' and '/foo' from callers, but always return one canonical URL.
  const endpointWithoutApiPrefix = normalizedEndpoint.replace(/^\/api(?=\/|$)/, '');

  return `${baseUrl}${endpointWithoutApiPrefix}`;
};

/**
 * What this workspace can actually search: its external API datasets (same
 * scoping the worker uses) plus the files in its vector store.
 */
export const fetchWorkspaceSources = async (token, workspaceId) => {
  const response = await axios.get(getWorkerApiUrl('/workspace/sources'), {
    headers: { Authorization: `Bearer ${token}` },
    params: workspaceId ? { workspace_id: workspaceId } : undefined
  });

  const data = response.data?.data || {};
  return {
    workspaceId: data.workspaceId || null,
    tools: Array.isArray(data.tools) ? data.tools : [],
    vectorStore: {
      id: data.vectorStore?.id || null,
      files: Array.isArray(data.vectorStore?.files) ? data.vectorStore.files : []
    }
  };
};

/**
 * Semantic search over the workspace's own documents. Results carry OpenAI's
 * own relevance score, so they are not re-scored lexically downstream.
 */
export const searchWorkspaceVectorStore = async (token, query, workspaceId, maxResults) => {
  const response = await axios.post(
    getWorkerApiUrl('/workspace/vector-store/search'),
    { query, workspaceId, maxResults },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const data = response.data?.data || {};
  return Array.isArray(data.sources) ? data.sources : [];
};

/**
 * Manual "search the web for more" escalation. Generation reaches this tier on
 * its own only when workspace sources come up empty.
 */
export const runWorkspaceWebSearch = async (token, query, maxResults) => {
  const response = await axios.post(
    getWorkerApiUrl('/workspace/web-search'),
    { query, maxResults },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const data = response.data?.data || {};
  return {
    sources: Array.isArray(data.sources) ? data.sources : [],
    allowedDomains: Array.isArray(data.allowedDomains) ? data.allowedDomains : []
  };
};

/**
 * Test external API through server-side proxy to avoid CSP restrictions
 * @param {object} apiConfig - API configuration to test
 * @returns {Promise<object>} - Test result
 */
export const testExternalApi = async (apiConfig) => {
  try {
    const token = localStorage.getItem('directus_token');
    const response = await fetchWorkerApi('/test-external-api', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
      url: apiConfig.url,
      method: apiConfig.method || 'GET',
      headers: apiConfig.headers || {},
      parameters: apiConfig.parameters || [],
      auth: apiConfig.auth || { type: 'none' }
      })
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      return {
        success: false,
        message: err?.message || `HTTP ${response.status}`,
        status: response.status,
        error: err?.error || 'REQUEST_FAILED'
      };
    }

    const payload = await response.json();

    return {
      success: true,
      status: payload.status,
      statusText: payload.statusText,
      data: payload.data,
      headers: payload.headers,
      message: 'API test successful'
    };
    
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Server-side API test failed:', error);
    
    return {
      success: false,
      message: error.response?.data?.message || error.message,
      status: error.response?.status,
      error: error.response?.data?.error || error.name
    };
  }
};